import { t } from '../i18n';
import {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  type FC,
} from 'react';
import { useParams } from 'react-router-dom';
import { useAppStore } from '../store/useAppStore';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchCourseProgress,
  saveLessonNotes,
  saveVideoProgressApi,
  resolveMediaUrl,
} from '../api/client';
import { Check, Clock, Eye, Headphones, Lock } from 'lucide-react';
import { QuizEngine } from './QuizEngine';
import { PageHeader } from '../components/PageHeader';
import { MaterialViewerModal } from '../components/MaterialViewerModal';
import FileTestViewer from '../components/FileTestViewer';
import { motion } from 'framer-motion';
import ReactPlayer from 'react-player';
import { usePresence } from '../hooks/usePresence';
import { BunnyVideo } from '../components/BunnyVideo';
import { HlsVideo } from '../components/HlsVideo';
import { VideoUnavailable } from '../components/VideoUnavailable';
import { LessonSidebar } from '../components/LessonSidebar';
import { useWideScreen } from '../hooks/useWideScreen';

export const LessonViewPage: FC = () => {
  const { lessonId } = useParams<{ lessonId: string }>();
  const { studentId, courseId, selectedLesson, coins } = useAppStore();

  const [isCompletedNow, setIsCompletedNow] = useState(false);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [isAudioMode, setIsAudioMode] = useState(false);
  const [notes, setNotes] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  const playerRef = useRef<any>(null);
  const [, setPlayedSecondsSet] = useState<Set<number>>(new Set());
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoCompleted, setVideoCompleted] = useState(false);
  const [videoProgressPercent, setVideoProgressPercent] = useState(0);

  const queryClient = useQueryClient();
  const lastPositionRef = useRef(0); // hozirgi soniya
  const savedPositionRef = useRef(-1); // serverga oxirgi yozilgani
  const seekedRef = useRef(false); // davom ettirish bir marta bajarilsin
  // Effektlar eng oxirgi funksiyani chaqirsin, lekin ular qayta yaralmasin
  const saveLastPositionRef = useRef<() => void>(() => {});
  const notesLoadedFor = useRef<string | null>(null);

  // Hook erta qaytishlardan OLDIN turishi shart (React #310).
  const online = usePresence(courseId, lessonId);
  /* Sayt koʻrinishi: darslar roʻyxati yonda turadi. */
  const wide = useWideScreen();

  /**
   * Test yoki topshiriq yuborilgandan keyin.
   *
   * MAHALLIY BAYROQ YETARLI EMAS. Ilgari bu yerda faqat
   * `setIsCompletedNow(true)` bor edi, ya'ni "bajarildi" belgisi
   * chiqardi-yu, `activeLesson` ning O'ZI keshdagi eski nusxa bo'lib
   * qolaverardi: ball, izoh, `fileTestStatus` va sarflangan urinishlar
   * eskiligicha. O'quvchi natijani ko'rish uchun sahifani qo'lda
   * yangilashga majbur edi — "javob darhol kelmayapti, refresh
   * berganda chiqyapti" degani aynan shu.
   *
   * Video yo'lida bu allaqachon to'g'ri qilingan edi; test va faylli
   * topshiriq yo'llari tushib qolgan.
   */
  const submitted = useCallback(() => {
    setIsCompletedNow(true);
    void queryClient.invalidateQueries({ queryKey: ['course-progress'] });
  }, [queryClient]);

  /*
   * Kurs progressi HAR DOIM olinadi.
   *
   * Ilgari bu so'rov `enabled: !selectedLesson` edi, ya'ni do'konda dars
   * bo'lsa u umuman yuborilmasdi. `selectedLesson` esa `CourseListPage`
   * bosilgan paytdagi MUZLATILGAN nusxa — u sessiya davomida hech qachon
   * yangilanmasdi. Oqibatlari:
   *
   *   - `testAttempts` eski qolardi: ekranda "1 / 2" turadi, serverda esa
   *     2 ta urinish sarflangan. "Testni boshlash" bosilganda server
   *     "urinishlar tugagan" deb rad etardi va bu qarama-qarshilikka
   *     o'xshab ko'rinardi;
   *   - `isVideoCompleted` ham shunday eskirardi.
   *
   * Endi yangi ma'lumot ustun, do'kondagi nusxa esa faqat birinchi
   * chizishda (so'rov hali kelmaganda) ishlatiladi.
   */
  const { data: course } = useQuery({
    queryKey: ['course-progress', studentId, courseId],
    queryFn: () => fetchCourseProgress(studentId!, courseId!),
    enabled: !!studentId && !!courseId,
  });

  const freshLesson = course?.modules
    .flatMap((m) => m.lessons)
    .find((l) => l.id === lessonId);

  /*
   * `useMemo` SHART.
   *
   * Bu obyekt effektlarning bog'liqligida turadi. Memosiz u har renderda
   * YANGI havola bo'lardi va quyidagi effekt to'xtovsiz qayta ishlar edi —
   * ya'ni `window.scrollTo({ top: 0 })` uzluksiz chaqirilib, sahifa pastga
   * surilishga qo'ymay, qaltirab yuqoriga tortib turardi.
   *
   * `moduleTitle` faqat do'kondagi nusxada bor — uni saqlab qolamiz,
   * qolgan hamma maydon serverdan keladi.
   */
  const activeLesson: any = useMemo(
    () =>
      freshLesson
        ? { moduleTitle: (selectedLesson as any)?.moduleTitle, ...freshLesson }
        : selectedLesson,
    [freshLesson, selectedLesson],
  );

  // Tanga moduli sozlamalari — kurs darajasida. O'chiq bo'lsa hech qanday
  // sotib olish tugmasi ko'rsatilmaydi.
  const coinSettings = (course as any)?.course?.coins;

  // Serverdan kelgan qiymatlar — PRIMITIV holida, effekt bog'liqligi uchun.
  const srvVideoCompleted = !!activeLesson?.isVideoCompleted;
  const srvWatchedPercent = activeLesson?.videoWatchedPercent ?? 0;

  // Dars ALMASHGANDA: sahifa boshiga va mahalliy hisob tozalanadi.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setPlayedSecondsSet(new Set());
    setVideoCompleted(false);
    setVideoProgressPercent(0);
    seekedRef.current = false;
    lastPositionRef.current = 0;
    savedPositionRef.current = -1;
    notesLoadedFor.current = null;
  }, [lessonId]);

  /*
   * Server qiymatlari faqat OLDINGA suradi.
   *
   * Ataylab `setVideoCompleted(srv)` emas, balki faqat `true` ga o'tkazish:
   * o'quvchi hozir ko'rib tugatgan bo'lsa, ayni paytda kelib qolgan eski
   * javob uni `false` ga qaytarib yuborishi mumkin edi. Foiz ham shunday —
   * `Math.max` mahalliy hisobni pasaytirmaydi.
   */
  useEffect(() => {
    if (srvVideoCompleted) setVideoCompleted(true);
  }, [srvVideoCompleted]);

  useEffect(() => {
    setVideoProgressPercent((p) => Math.max(p, srvWatchedPercent));
  }, [srvWatchedPercent]);

  // Qaydlar dars boshiga bir marta yuklanadi — aks holda o'quvchi yozib
  // turganda kelgan javob uning matnini o'chirib yuborardi.
  useEffect(() => {
    if (!activeLesson || notesLoadedFor.current === lessonId) return;
    notesLoadedFor.current = lessonId ?? null;
    setNotes(activeLesson.notes || '');
  }, [lessonId, activeLesson]);

  // Sahifadan chiqishda oxirgi joyni saqlaymiz
  useEffect(() => {
    return () => saveLastPositionRef.current();
  }, [lessonId]);

  // Har 10 soniyada bir marta — har `timeupdate` da so'rov yubormaslik uchun
  useEffect(() => {
    const id = setInterval(() => saveLastPositionRef.current(), 10_000);
    return () => clearInterval(id);
  }, []);

  const handleVideoProgress = (state: {
    playedSeconds: number;
    played: number;
  }) => {
    if (!studentId || !lessonId || !activeLesson) return;

    // Joyni HAR DOIM kuzatamiz — video allaqachon ko'rilgan bo'lsa ham,
    // majburiy bo'lmasa ham. Davom ettirish ko'rish talabidan mustaqil:
    // o'quvchi 10-daqiqada to'xtatgan bo'lsa, o'sha yerdan davom etsin.
    const sec = Math.floor(state.playedSeconds);
    lastPositionRef.current = sec;

    // Quyidagisi esa faqat "ko'rildi" chegarasini hisoblash uchun.
    if (videoCompleted || !activeLesson.isVideoRequired) return;

    setPlayedSecondsSet((prev) => {
      const next = new Set(prev);
      next.add(sec);

      if (videoDuration > 0) {
        const percent = Math.floor((next.size / videoDuration) * 100);
        // `Math.max` — ko'rsatkich pasaymasin: o'quvchi qayta ko'rsa
        // mahalliy hisob 0 dan boshlanadi, serverdagi 90% esa haqiqat.
        setVideoProgressPercent((p) => Math.max(p, Math.min(percent, 100)));
        const target = activeLesson.videoWatchPercent || 80;

        if (percent >= target && !videoCompleted) {
          setVideoCompleted(true);
          saveVideoProgressApi(lessonId, percent, true, sec)
            /*
             * Keshni YANGILASH SHART.
             *
             * `course-progress` keshida `isVideoCompleted: false` qolib
             * ketardi va o'quvchi darslar ro'yxatiga qaytib qayta kirganda
             * `selectedLesson` o'sha eski nusxadan yasalardi — natijada
             * video QAYTA talab qilinardi. Server to'g'ri saqlab turgan
             * bo'lsa ham, mijoz uni hech qachon qayta o'qimasdi.
             */
            .then(() =>
              queryClient.invalidateQueries({
                queryKey: ['course-progress'],
              }),
            )
            .catch(console.error);
        }
      }
      return next;
    });
  };

  /*
   * Qayerda to'xtagan bo'lsa — o'sha joydan.
   *
   * Foizdan farqli o'laroq bu eng yuqori nuqta emas, OXIRGI joy. Har
   * soniyada so'rov yubormaslik uchun 10 soniyada bir marta saqlanadi,
   * hamda sahifadan chiqishda bir marta.
   */
  const saveLastPosition = () => {
    const sec = lastPositionRef.current;
    if (!lessonId || sec <= 0 || sec === savedPositionRef.current) return;
    savedPositionRef.current = sec;
    saveVideoProgressApi(
      lessonId,
      videoProgressPercent,
      videoCompleted,
      sec,
    ).catch(() => {});
  };
  saveLastPositionRef.current = saveLastPosition;

  /** Saqlangan joydan davom ettirish — bir marta, metadata yuklangach. */
  const resume = (el: HTMLVideoElement | undefined | null) => {
    if (!el || seekedRef.current || !el.duration) return;
    seekedRef.current = true;
    const joy = activeLesson?.videoPositionSec || 0;
    // Oxiriga yetgan bo'lsa boshidan — aks holda video darhol tugab qolardi
    if (joy > 0 && joy < el.duration - 10) {
      el.currentTime = joy;
      lastPositionRef.current = joy;
      savedPositionRef.current = joy;
    }
  };

  const handleNotesBlur = async () => {
    if (!studentId || !lessonId) return;
    try {
      setIsSavingNotes(true);
      await saveLessonNotes(studentId, lessonId, notes);
    } finally {
      setIsSavingNotes(false);
    }
  };

  if (!activeLesson) {
    return (
      <>
        <PageHeader title="Dars" />
        <main className="page">
          <div className="skeleton skeleton-card" style={{ height: 200 }} />
          <div className="skeleton skeleton-line" style={{ width: '70%' }} />
          <div className="skeleton skeleton-line" style={{ width: '45%' }} />
        </main>
      </>
    );
  }

  const resolvedVideoUrl = resolveMediaUrl(activeLesson.videoUrl);
  const materialUrl =
    activeLesson.materials || activeLesson.materialUrl || null;
  const isDone = activeLesson.isCompleted || isCompletedNow;
  const videoUnavailable = !!activeLesson.videoUnavailable;
  /*
   * To'rtta manba bir vaqtda yashaydi (migratsiya bosqichma-bosqich):
   *   HLS oqimi      — Bunny videolari (ASOSIY yo'l)
   *   Bunny iframe   — CDN kalitlari qo'yilmagan bo'lsa, zaxira
   *   `<video>`      — `/uploads/...` va imzolangan `/api/video/...`
   *   ReactPlayer    — YouTube va boshqa tashqi havolalar
   */
  const isHlsVideo = !!resolvedVideoUrl && resolvedVideoUrl.includes('.m3u8');
  const isBunnyVideo =
    !isHlsVideo &&
    !!resolvedVideoUrl &&
    resolvedVideoUrl.includes('mediadelivery.net');
  const isNativeVideo =
    !isHlsVideo &&
    !isBunnyVideo &&
    resolvedVideoUrl &&
    (resolvedVideoUrl.startsWith('/') ||
      resolvedVideoUrl.toLowerCase().endsWith('.mp4'));

  /*
   * SAYT QOBIGʻI — dars ekrani ikki ustunda.
   *
   * Telefonda dars butun sahifani egallaydi va oʻquvchi roʻyxatga
   * QAYTIB borib keyingisini tanlaydi. Kompyuterda bu ortiqcha ikki
   * qadam, shuning uchun roʻyxat yonda turadi.
   *
   * Bu CSS bilan hal boʻlmaydi: media soʻrovi elementni yashira
   * oladi, lekin boshqa TUZILMA chiza olmaydi. Mantiq esa umumiy —
   * faqat qobiq boshqacha.
   */
  const shell = (content: React.ReactNode) =>
    wide && course ? (
      <div className="two-column">
        <LessonSidebar course={course} currentId={lessonId} />
        <div>{content}</div>
      </div>
    ) : (
      content
    );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <PageHeader
        title={activeLesson.title}
        subtitle={activeLesson.moduleTitle || t('Dars')}
        action={
          isDone ? (
            <span className="chip chip-success">
              <Check size={12} aria-hidden="true" /> {t('Yakunlandi')}
            </span>
          ) : undefined
        }
      />

      <main className="page">
        {shell(
          <>
            {/* Har doim ko'rinadi — yolg'iz bo'lsa ham (matn boshqacha). */}
            {
              <p
                className="text-xs"
                style={{
                  marginBottom: 'var(--space-4)',
                  color: 'var(--ok)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: 'var(--ok)',
                    flex: 'none',
                  }}
                />
                {online.lesson > 0 ? (
                  <>
                    {t('Bu darsni hozir siz va yana {count} kishi koʻryapti', {
                      count: online.lesson,
                    })}
                  </>
                ) : (
                  <>{t('Bu darsni hozir faqat siz koʻryapsiz')}</>
                )}
              </p>
            }

            {/* ------------------------------------------------------------ video */}
            {videoUnavailable && (
              <section style={{ marginBottom: 'var(--space-5)' }}>
                <VideoUnavailable />
              </section>
            )}
            {resolvedVideoUrl && (
              <section style={{ marginBottom: 'var(--space-5)' }}>
                <div
                  style={{
                    position: 'relative',
                    borderRadius: 'var(--radius-md)',
                    overflow: 'hidden',
                    border: '1px solid var(--border)',
                    background: '#000',
                  }}
                >
                  {isAudioMode && (
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 10,
                        display: 'grid',
                        placeItems: 'center',
                        background: 'var(--surface-sunken)',
                        color: 'var(--ink-secondary)',
                        pointerEvents: 'none',
                        textAlign: 'center',
                        padding: 'var(--space-4)',
                      }}
                    >
                      <div>
                        <Headphones size={26} aria-hidden="true" />
                        <p className="text-sm" style={{ marginTop: 6 }}>
                          {t(
                            'Audio rejim — ekran oʻchirilgan, ovoz davom etadi',
                          )}
                        </p>
                      </div>
                    </div>
                  )}

                  {isHlsVideo ? (
                    <HlsVideo
                      src={resolvedVideoUrl!}
                      videoRef={playerRef}
                      style={{ width: '100%', height: 210, background: '#000' }}
                      onTimeUpdate={(e) => {
                        const t = e.currentTarget;
                        if (!t.duration) return;
                        setVideoDuration(t.duration);
                        handleVideoProgress({
                          playedSeconds: t.currentTime,
                          played: t.currentTime / t.duration,
                        });
                      }}
                      onLoadedMetadata={(e) => {
                        setVideoDuration(e.currentTarget.duration);
                        resume(e.currentTarget);
                      }}
                    />
                  ) : isBunnyVideo ? (
                    <BunnyVideo
                      src={resolvedVideoUrl!}
                      startAt={activeLesson.videoPositionSec || 0}
                      onDuration={setVideoDuration}
                      onProgress={handleVideoProgress}
                    />
                  ) : isNativeVideo ? (
                    <video
                      ref={playerRef}
                      src={resolvedVideoUrl}
                      controls
                      playsInline
                      controlsList="nodownload"
                      style={{ width: '100%', height: 210, background: '#000' }}
                      onTimeUpdate={(e) => {
                        const t = e.currentTarget;
                        if (!t.duration) return;
                        setVideoDuration(t.duration);
                        handleVideoProgress({
                          playedSeconds: t.currentTime,
                          played: t.currentTime / t.duration,
                        });
                      }}
                      onLoadedMetadata={(e) => {
                        setVideoDuration(e.currentTarget.duration);
                        resume(e.currentTarget);
                      }}
                    />
                  ) : (
                    <ReactPlayer
                      ref={playerRef}
                      src={resolvedVideoUrl}
                      controls
                      width="100%"
                      height="210px"
                      playsInline
                      onLoadedMetadata={(e: any) => {
                        const t = e?.currentTarget as
                          HTMLVideoElement | undefined;
                        if (t?.duration) setVideoDuration(t.duration);
                        resume(t);
                      }}
                      onTimeUpdate={(e: any) => {
                        const t = e?.currentTarget as
                          HTMLVideoElement | undefined;
                        if (!t?.duration) return;
                        setVideoDuration(t.duration);
                        handleVideoProgress({
                          playedSeconds: t.currentTime,
                          played: t.currentTime / t.duration,
                        });
                      }}
                    />
                  )}
                </div>

                <div className="spread" style={{ marginTop: 'var(--space-3)' }}>
                  {activeLesson.isVideoRequired ? (
                    /*
                     * Talab bajarilgach FOIZ EMAS, "bajarildi" ko'rsatiladi.
                     *
                     * Ilgari saqlangan foiz turaverardi — masalan "85% / 80%".
                     * Hisoblagich esa talab bajarilgandan keyin ataylab
                     * to'xtaydi (pasaymasligi uchun), ya'ni o'quvchi videoni
                     * qayta ko'rsa ham raqam qimirlamasdi. Tashqaridan bu
                     * "hisoblagich singan" bo'lib ko'rinardi — aynan shu savol
                     * tug'ildi ham.
                     */
                    videoCompleted ? (
                      <span
                        className="text-xs"
                        style={{ color: 'var(--success)' }}
                      >
                        <Check size={12} aria-hidden="true" />{' '}
                        {t('Video koʻrildi')}
                      </span>
                    ) : (
                      <span className="text-xs muted">
                        {t('Koʻrildi: {watched}% / {required}%', {
                          watched: videoProgressPercent,
                          required: activeLesson.videoWatchPercent || 80,
                        })}
                      </span>
                    )
                  ) : (
                    <span />
                  )}
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setIsAudioMode(!isAudioMode)}
                    aria-pressed={isAudioMode}
                  >
                    <Headphones size={14} aria-hidden="true" />
                    {t('Audio rejim')}
                  </button>
                </div>

                {activeLesson.isVideoRequired && (
                  <div className="meter" style={{ marginTop: 6 }}>
                    <div
                      className={`meter-fill${videoCompleted ? ' is-complete' : ''}`}
                      style={{ width: `${videoProgressPercent}%` }}
                    />
                  </div>
                )}
              </section>
            )}

            {/* --------------------------------------------------------- material */}
            {materialUrl && (
              <section
                className="card-quiet spread"
                style={{ marginBottom: 'var(--space-5)' }}
              >
                <div style={{ minWidth: 0 }}>
                  <div className="lesson-title">{t('Dars materiali')}</div>
                  <div className="lesson-meta">{t('PDF yoki hujjat')}</div>
                </div>
                <div
                  className="row"
                  style={{ gap: 'var(--space-2)', flex: 'none' }}
                >
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setIsViewerOpen(true)}
                  >
                    <Eye size={14} aria-hidden="true" />
                    {t('Ochish')}
                  </button>
                </div>
              </section>
            )}

            {/* ---------------------------------------------------------- konspekt */}
            {activeLesson.content && (
              <section
                className="card"
                style={{ marginBottom: 'var(--space-5)' }}
              >
                <h2
                  style={{
                    fontSize: 'var(--text-md)',
                    marginBottom: 'var(--space-3)',
                  }}
                >
                  {t('Konspekt')}
                </h2>
                <div className="prose" style={{ whiteSpace: 'pre-line' }}>
                  {activeLesson.content}
                </div>
              </section>
            )}

            {/* ------------------------------------------------------------ qaydlar */}
            <section
              className="card"
              style={{ marginBottom: 'var(--space-6)' }}
            >
              <div
                className="spread"
                style={{ marginBottom: 'var(--space-2)' }}
              >
                <label
                  className="field-label"
                  htmlFor="lesson-notes"
                  style={{ fontSize: 'var(--text-md)' }}
                >
                  {t('Shaxsiy qaydlar')}
                </label>
                {isSavingNotes && (
                  <span className="text-xs muted">{t('Saqlanmoqda…')}</span>
                )}
              </div>
              <p
                className="field-hint"
                style={{ marginBottom: 'var(--space-3)' }}
              >
                {t('Faqat sizga koʻrinadi va saqlanib qoladi.')}
              </p>
              <textarea
                id="lesson-notes"
                className="textarea"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={handleNotesBlur}
                placeholder={t('Masalan: 05:23 — yangi formula')}
              />
            </section>

            {/* --------------------------------------------------------------- test */}
            {/*
              Video yo'q bo'lsa "ko'ring" talabi o'quvchini butunlay
              to'xtatib qo'yardi — aybi bo'lmagan holda. Talab faqat shu
              ekranda (server `startTest` da tekshirmaydi).
            */}
            {activeLesson.isVideoRequired &&
            !videoCompleted &&
            !videoUnavailable ? (
              <div className="callout callout-warning">
                <Lock size={17} style={{ flex: 'none' }} aria-hidden="true" />
                <span>
                  {t('Testni ochish uchun videoni kamida {percent}% koʻring.', {
                    percent: activeLesson.videoWatchPercent || 80,
                  })}
                </span>
              </div>
            ) : activeLesson.deadlineMissed ? (
              <div className="callout callout-danger">
                <Clock size={17} style={{ flex: 'none' }} aria-hidden="true" />
                <span>
                  {t(
                    'Bu darsning muddati oʻtgan. Qayta ochish uchun ustozingizga murojaat qiling.',
                  )}
                </span>
              </div>
            ) : activeLesson.testType === 'FILE_TEST' ? (
              <FileTestViewer lesson={activeLesson} onSuccess={submitted} />
            ) : (
              <QuizEngine
                lessonId={activeLesson.id}
                tests={activeLesson.tests || []}
                onCompleteSuccess={submitted}
                isAlreadyCompleted={isDone}
                maxAttempts={activeLesson.testMaxAttempts || 0}
                timeLimitSec={activeLesson.testTimeLimitSec || 0}
                currentAttempts={activeLesson.testAttempts || 0}
                extraAttempts={activeLesson.extraAttempts || 0}
                coinPriceExtraAttempt={
                  coinSettings?.enabled ? coinSettings.extraAttempt || 0 : 0
                }
                coins={coins}
                onBought={() => setIsCompletedNow(false)}
                analysisVideoUrl={activeLesson.analysisVideoUrl || null}
                analysisVideoUnavailable={
                  !!activeLesson.analysisVideoUnavailable
                }
              />
            )}

            <MaterialViewerModal
              isOpen={isViewerOpen}
              onClose={() => setIsViewerOpen(false)}
              materialUrl={materialUrl}
              title={activeLesson.title}
            />
          </>,
        )}
      </main>
    </motion.div>
  );
};
